import { useCallback, useEffect, useRef, useState } from 'react'

/** Marks an element that takes file drops on the wizard page; file drags anywhere else are refused. */
export const WIZARD_DROP_ATTRIBUTE = 'data-wizard-drop'

export const carriesFiles = (event: DragEvent): boolean => Array.from(event.dataTransfer?.types ?? []).includes('Files')

const inWizardDropZone = (target: EventTarget | null): boolean => {
  const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null
  return !!element?.closest(`[${WIZARD_DROP_ATTRIBUTE}]`)
}

/**
 * Whether files are being dragged over the window, and the page's ownership of file drops while it is
 * mounted: a file drag over anything but a wizard drop zone is refused and stopped at the window, so no
 * other handler — the app-wide uploader in particular — ever sees it.
 */
export const useFileDrag = (): { dragging: boolean; end: () => void } => {
  const [dragging, setDragging] = useState(false)
  /* dragenter and dragleave come in pairs for every element crossed; the drag is over only at zero */
  const depth = useRef(0)
  const end = useCallback(() => {
    depth.current = 0
    setDragging(false)
  }, [])

  useEffect(() => {
    const enter = (event: DragEvent) => {
      if (carriesFiles(event)) {
        depth.current += 1
        setDragging(true)
      }
    }
    const leave = (event: DragEvent) => {
      if (carriesFiles(event)) {
        depth.current = Math.max(0, depth.current - 1)
        if (depth.current === 0) {
          setDragging(false)
        }
      }
    }
    const guard = (event: DragEvent) => {
      if (!carriesFiles(event)) {
        return
      }
      event.preventDefault()
      if (!inWizardDropZone(event.target)) {
        event.stopPropagation()
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = 'none'
        }
      }
      if (event.type === 'drop') {
        /* after this event has reached the drop zone, which a synchronous unmount could take away */
        window.setTimeout(end, 0)
      }
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        end()
      }
    }
    window.addEventListener('dragenter', enter, true)
    window.addEventListener('dragleave', leave, true)
    window.addEventListener('dragover', guard, true)
    window.addEventListener('drop', guard, true)
    window.addEventListener('dragend', end, true)
    window.addEventListener('keydown', escape)
    return () => {
      window.removeEventListener('dragenter', enter, true)
      window.removeEventListener('dragleave', leave, true)
      window.removeEventListener('dragover', guard, true)
      window.removeEventListener('drop', guard, true)
      window.removeEventListener('dragend', end, true)
      window.removeEventListener('keydown', escape)
    }
  }, [end])

  return { dragging, end }
}
